import {createApi} from '@reduxjs/toolkit/query/react';
import {customAxiosBaseQuery} from '../API/customAxiosBaseQuery';
import {BaseResponseType} from '../types/TypesStore';
import {
	StudyExpert,
	UserInfoForEdit,
	UserRefLink,
	UserStatTree,
	GenerateCodeData,
	ApproveSmsCode,
} from '../types/TypesUser';
import {SessionCart} from '../types/TypesSession';
import {userAPI, clientsAPI} from '../API/api';
import {
	createQueryEndpoint,
	createMutationEndpoint,
	createSimpleQueryEndpoint,
	createMutationEndpointWithHandlers,
	createQueryEndpointWithHandlers,
} from './utils/apiFactory';

export const userApi = createApi({
	reducerPath: 'userApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: [
		'userInfo',
		'userStatTree',
		'userRef',
		'studyStatus',
		'session',
		'cart',
		'smsCode',
	],
	refetchOnMountOrArgChange: 30, // Перезапрашивать данные каждые 30 секунд
	refetchOnFocus: true, // Перезапрашивать при фокусе на окне
	refetchOnReconnect: true, // Перезапрашивать при восстановлении соединения
	endpoints: (builder) => ({
		// Получение информации пользователя для редактирования
		getUserInfo: builder.query<BaseResponseType & UserInfoForEdit, void>({
			...createSimpleQueryEndpoint(userAPI.getUserInfo, ['userInfo']),
		}),
		checkPassword: builder.mutation<BaseResponseType, string>({
			query: (password) => ({
				func: clientsAPI.checkPassword,
				args: [password],
			}),
			invalidatesTags: ['userInfo'],
		}),
		// Получение статистики дерева пользователя
		getUserStatTree: builder.query<BaseResponseType & UserStatTree, string>({
			...createQueryEndpoint(userAPI.getUserStat, ['userStatTree']),
		}),

		// Получение реферальной ссылки пользователя
		getUserRef: builder.query<BaseResponseType & {links: UserRefLink[]}, void>({
			...createSimpleQueryEndpoint(userAPI.getUserRef, ['userRef']),
		}),

		// Получение статуса обучения пользователя
		getStudyStatus: builder.query<
			BaseResponseType & {experts: StudyExpert[]},
			void
		>({
			...createQueryEndpointWithHandlers(
				userAPI.getStudyStatus,
				['studyStatus'],
				undefined,
				(response: any) => {
					console.error('Error fetching study status:', response);
					return response;
				},
			),
		}),

		// Редактирование информации пользователя (мутация)
		editUserInfo: builder.mutation<BaseResponseType, any>({
			...createMutationEndpointWithHandlers(
				userAPI.getEditUserInfo,
				['userInfo'],
				undefined,
				(response: any) => {
					console.error('Error editing user info:', response);
					return response;
				},
			),
		}),

		addUserPhoto: builder.mutation<BaseResponseType & {href: string}, File>({
			...createMutationEndpoint(userAPI.addUserPhoto, ['userInfo']),
		}),

		// Авторизация (getStart)
		getStart: builder.query<
			BaseResponseType & {token?: string; api_token?: string},
			void
		>({
			query: () => {
				const loginType = process.env.REACT_APP_LOGIN as 'login' | 'laraLogin';
				const loginFn = userAPI[loginType];

				if (typeof loginFn !== 'function') {
					throw new Error(`Invalid login method: ${loginType}`);
				}

				return {
					func: loginFn,
					args: [],
				};
			},
			providesTags: ['session'],
		}),

		// Получение информации о корзине
		getCart: builder.query<
			BaseResponseType & {response: SessionCart; pay_money: number},
			void
		>({
			...createSimpleQueryEndpoint(userAPI.getCartInfo, ['cart']),
		}),

		// Генерация SMS кода для регистрации
		generateCode: builder.mutation<
			BaseResponseType & {phone: string; refcode: string},
			GenerateCodeData
		>({
			...createMutationEndpointWithHandlers(
				clientsAPI.sendSms,
				[],
				(response: any) => {
					if (response.approve && response.phone && response.refcode) {
						const regClientInfo = {
							phone: response.phone,
							refcode: response.refcode,
						};
						localStorage.setItem(
							'choiceRegClient',
							JSON.stringify(regClientInfo),
						);
					}
					return response;
				},
				(response: any) => {
					console.error('Error generating SMS code:', response);
					return response;
				},
			),
		}),

		// Подтверждение SMS кода
		approveCode: builder.mutation<
			BaseResponseType & {phone: string; refcode: string},
			ApproveSmsCode
		>({
			...createMutationEndpointWithHandlers(
				clientsAPI.approveCodeSms,
				[],
				(response: any) => {
					if (response.approve) {
						localStorage.setItem('choiceRegClient', JSON.stringify(response));
					}
					return response;
				},
				(response: any) => {
					console.error('Error approving SMS code:', response);
					return response;
				},
			),
		}),
	}),
});

export const {
	useGetUserInfoQuery,
	useGetUserStatTreeQuery,
	useGetUserRefQuery,
	useGetStudyStatusQuery,
	useEditUserInfoMutation,
	useAddUserPhotoMutation,
	useGetCartQuery,
	useGenerateCodeMutation,
	useApproveCodeMutation,
	useCheckPasswordMutation,
} = userApi;
